<!--ABOUTME: Terms of Use page displaying current terms of use with version information-->
<!--ABOUTME: Static page that shows terms of use content with proper formatting and navigation-->

<template>
    <div class="policy-page">
        <div class="policy-container">
            <div class="policy-header">
                <h1>Terms of Use</h1>
                <div class="policy-meta">
                    <span class="version">Version 1.0</span>
                    <span class="separator">•</span>
                    <span class="effective-date"
                        >Effective Date: 08 September 2025</span
                    >
                </div>
            </div>

            <div class="policy-content">
                <div class="provider-info">
                    <p>
                        <strong>Provider:</strong> Stekz B.V., Groningen,
                        Netherlands —
                        <a href="mailto:legal@stekz.com">legal@stekz.com</a>
                    </p>
                </div>

                <h2>1) Scope &amp; Accounts</h2>
                <p>
                    Business use only; you represent a business, you're
                    authorised and 16+.<br />
                    Keep credentials confidential. You're responsible for your
                    accounts and activity.<br />
                    We may suspend or terminate for risk, legal, or AUP reasons
                    at any time.
                </p>

                <h2>2) Your Content &amp; License</h2>
                <p>
                    <strong>Ownership.</strong> You own all code, files and
                    materials you upload ("Customer Content").
                </p>
                <p>
                    <strong>License to operate.</strong> You grant Stekz a
                    non-exclusive, worldwide, royalty-free license to host, run,
                    transmit, display, and create purely technical copies of
                    Customer Content solely to provide, secure, troubleshoot,
                    and improve the Service.
                </p>
                <p>
                    <strong>No training by default.</strong> We do not train
                    general models on private Customer Content unless you opt-in
                    clearly. We may use aggregated/anonymous telemetry to run
                    and improve the Service.
                </p>

                <h2>3) Acceptable Use</h2>
                <p>
                    You must follow the AUP (below). We can throttle, block, or
                    remove content, and suspend accounts that breach it or
                    create material risk.
                </p>

                <h2>4) Free Service; No SLA</h2>
                <p>
                    The Service is provided "AS IS" without warranties or
                    commitments (uptime, support, backups, RPO/RTO).<br />
                    We can change or discontinue features, impose or adjust
                    quotas/rate limits at any time.
                </p>

                <h2>5) Integrations &amp; Third Parties</h2>
                <p>
                    You may connect third-party services at your discretion and
                    risk. Stekz isn't responsible for third-party terms, acts,
                    or omissions.
                </p>

                <h2>6) IP; Feedback</h2>
                <p>
                    No IP rights transfer.<br />
                    Feedback you provide may be used freely by Stekz without
                    restriction or attribution.
                </p>

                <h2>7) Privacy, Data Roles &amp; Subprocessors</h2>
                <p>
                    For Account/Admin Data, Stekz is a controller (see Privacy
                    Policy).<br />
                    For Customer Content, Stekz is a processor under the DPA
                    (below).<br />
                    Hosting &amp; compute run on Google Cloud (named
                    subprocessor).
                </p>

                <h2>8) Security &amp; Incidents</h2>
                <p>
                    We apply reasonable technical and organisational measures
                    (see DPA Annex). If we become aware of a breach of Customer
                    Content, we'll notify without undue delay.
                </p>

                <h2>9) DMCA/Notice-and-Takedown</h2>
                <p>
                    Report alleged infringement to
                    <a href="mailto:legal@stekz.com">legal@stekz.com</a>. We may
                    remove content and disable repeat infringers.
                </p>

                <h2>10) Term; Deletion; Inactivity</h2>
                <p>
                    You can stop using the Service any time.<br />
                    On termination/suspension we may delete Customer Content
                    after 30 days (free plan).<br />
                    <strong>Inactivity deletion:</strong> we may delete free
                    accounts and their data after 90 days of no sign-in.
                </p>

                <h2>11) Export Controls &amp; High-Risk Use</h2>
                <p>
                    You won't use the Service where prohibited by
                    sanctions/export laws.<br />
                    Not for high-risk scenarios (medical devices, life support,
                    critical infrastructure, aviation, etc.).
                </p>

                <h2>12) Warranty Disclaimer &amp; Liability Limit</h2>
                <p>
                    <strong>No warranties.</strong> To the fullest extent
                    permitted by law, the Service is provided AS IS.
                </p>
                <p>
                    <strong>Limitation.</strong> Stekz won't be liable for
                    indirect, incidental, special, punitive, or consequential
                    damages, or for lost profits/revenue/data/goodwill.
                </p>
                <p>
                    <strong>Cap.</strong> For free accounts, Stekz's total
                    liability is €100 in aggregate. Mandatory law carve-outs
                    apply.
                </p>

                <h2>13) Changes to Terms</h2>
                <p>
                    We may update terms; material changes take effect 30 days
                    after posting (immediate for AUP/security/legal). Continued
                    use = acceptance.
                </p>

                <h2>14) Governing Law &amp; Venue</h2>
                <p>
                    Dutch law (excluding conflict-of-laws and CISG). Exclusive
                    venue: courts of Noord-Nederland (Groningen).
                </p>

                <hr class="section-divider" />

                <h1>Acceptable Use Policy (AUP) — MyStekz</h1>
                <p><strong>You must not:</strong></p>
                <ul>
                    <li>
                        Break the law; infringe IP; violate privacy or publicity
                        rights.
                    </li>
                    <li>
                        Upload malware, backdoors, or attempt to probe, scan, or
                        bypass security.
                    </li>
                    <li>
                        Misuse resources: crypto-mining, traffic flooding,
                        abusive automation, rate-limit evasion, excessive
                        scraping or API abuse.
                    </li>
                    <li>
                        Host or share: child sexual abuse material,
                        exploitation, doxxing, threats, extremist or terror
                        content, or unlawful hate content.
                    </li>
                    <li>
                        Interfere with others' use of the Service, or attempt
                        account hijacking.
                    </li>
                    <li>
                        Use the Service for high-risk activities
                        (safety-critical/medical/aviation/energy control).
                    </li>
                    <li>
                        Misrepresent identity, forge headers, or run spam or
                        unsolicited bulk messages.
                    </li>
                    <li>
                        Collect or process special categories of data without
                        appropriate legal basis and safeguards.
                    </li>
                </ul>
                <p>
                    <strong>Enforcement:</strong> We may remove content,
                    throttle, or suspend/terminate accounts immediately to
                    protect users, infrastructure, or comply with law.
                </p>

                <hr class="section-divider" />

                <h1>Data Processing Addendum — MyStekz</h1>
                <p>
                    This DPA forms part of the Terms and applies when Stekz
                    processes Customer Content on your behalf under GDPR.
                </p>
                <p>
                    <strong>Parties.</strong> Controller: You (the customer).
                    Processor: Stekz B.V., Groningen, Netherlands (<a
                        href="mailto:legal@stekz.com"
                        >legal@stekz.com</a
                    >).
                </p>

                <h2>1) Subject-Matter, Nature, Duration</h2>
                <p>
                    <strong>Purpose:</strong> Hosting, storage, compute,
                    transmission, indexing, and security processing of Customer
                    Content to provide the Service.<br />
                    <strong>Nature:</strong> Automated and occasional manual
                    processing (support/security).<br />
                    <strong>Duration:</strong> Term of your use of the Service,
                    plus deletion period in Section 8.
                </p>

                <h2>2) Types of Data &amp; Data Subjects</h2>
                <p>
                    <strong>Data:</strong> Code, files, text, project metadata,
                    logs, identifiers, and other content you submit.<br />
                    <strong>Data Subjects:</strong> Your users, collaborators,
                    and individuals whose data appears in Customer Content.<br />
                    <strong>Special categories:</strong> Not intended; you must
                    not upload unless lawfully permitted and agreed.
                </p>

                <h2>3) Roles &amp; Instructions</h2>
                <p>
                    Stekz acts solely on your documented instructions (the
                    Terms, this DPA, your configurations, and written
                    instructions).<br />
                    If an instruction is unlawful, Stekz will inform
                    you—processing may be suspended.
                </p>

                <h2>4) Confidentiality &amp; Personnel</h2>
                <p>
                    Stekz ensures personnel are bound by confidentiality and
                    receive data-protection training. Access follows
                    least-privilege principles.
                </p>

                <h2>5) Security Measures</h2>
                <p>
                    Stekz implements appropriate technical and organisational
                    measures (see Annex A), including encryption in transit,
                    access controls, logging, vulnerability management, and
                    hardened cloud infrastructure on Google Cloud.
                </p>

                <h2>6) Subprocessors</h2>
                <p>
                    You authorise Stekz to use subprocessors. The current list
                    of subprocessors:
                </p>
                <ul>
                    <li>Google Cloud</li>
                    <li>Firebase</li>
                    <li>Mixpanel</li>
                    <li>OpenAI</li>
                    <li>Anthropic</li>
                    <li>Sentry</li>
                    <li>CloudFlare</li>
                </ul>
                <p>
                    Stekz imposes GDPR-equivalent obligations on subprocessors
                    and remains responsible for their performance.<br />
                    We will notify of material changes and allow objection for
                    reasonable, documented grounds related to data protection.
                </p>

                <h2>7) International Transfers</h2>
                <p>
                    Where Customer Content is transferred outside the EEA/UK,
                    Stekz will use appropriate safeguards, incl. EU SCCs (Module
                    2) and UK addendum where relevant, plus transfer-impact
                    assessments as appropriate.
                </p>

                <h2>8) Data Subject Requests; Assistance</h2>
                <p>
                    Stekz will assist, within the nature of the Service, with
                    data-subject requests, DPIAs, and consultations, considering
                    the free-tier nature and proportionality.
                </p>

                <h2>9) Breach Notification</h2>
                <p>
                    Upon becoming aware of a Personal Data Breach affecting
                    Customer Content, Stekz will notify without undue delay and
                    share available details and remediation steps.
                </p>

                <h2>10) Audits</h2>
                <p>
                    On reasonable written notice, once per 12 months, Stekz will
                    provide independent audit reports or summaries (e.g.,
                    SOC/ISO attestations where available) and answer reasonable
                    security questionnaires. On-site audits are available only
                    where required by law, limited in scope/time, and at your
                    cost.
                </p>

                <h2>11) Return &amp; Deletion</h2>
                <p>
                    On termination or your written request, Stekz will delete
                    Customer Content or return a copy (if technically feasible)
                    within 30 days, subject to lawful retention obligations and
                    backups lifecycle. Free tier: no specific RPO/RTO
                    commitments.
                </p>

                <h2>12) Liability &amp; Order of Precedence</h2>
                <p>
                    Parties' liability follows the Terms of Use. If there's a
                    conflict, this DPA prevails over the Terms for
                    data-protection matters.
                </p>

                <h2>Annex A — Security Measures (Summary)</h2>
                <p>
                    <strong>Infrastructure &amp; Hosting:</strong> Customer
                    Content hosted on Google Cloud with data-centre physical
                    security, segregation, and redundancy features.
                </p>
                <p>
                    <strong>Encryption:</strong> TLS in transit; encryption at
                    rest using cloud-native encryption.
                </p>
                <p>
                    <strong>Access Control:</strong> SSO/OAuth support where
                    available; role-based access; least-privilege; MFA for admin
                    access; key management via cloud KMS.
                </p>
                <p>
                    <strong>App Security:</strong> Secure SDLC, code review,
                    dependency scanning, vulnerability management, regular
                    patching.
                </p>
                <p>
                    <strong>Network Security:</strong> Firewalls, VPC isolation,
                    DDoS protections, rate-limiting, WAF/CDN where applicable.
                </p>
                <p>
                    <strong>Monitoring &amp; Logging:</strong> Centralised
                    logging, alerting, anomaly detection, and security event
                    response runbooks.
                </p>
                <p>
                    <strong>Backups &amp; Recovery:</strong> Periodic backups
                    with restricted access; no free-tier RPO/RTO commitments.
                </p>
                <p>
                    <strong>Incident Response:</strong> Defined IRP, breach
                    assessment and customer notification process.
                </p>
                <p>
                    <strong>Personnel &amp; Training:</strong> Security
                    awareness, background checks as permitted by law,
                    confidentiality agreements.
                </p>
                <p>
                    <strong>Compliance:</strong> Risk assessments and
                    third-party attestations where available (e.g., ISO/SOC).
                </p>
            </div>

            <div class="policy-footer">
                <p class="last-updated">Last updated: 08 September 2025</p>
                <div class="contact-info">
                    <p>Questions about these Terms of Use? Contact us at:</p>
                    <p>
                        <strong
                            ><a href="mailto:legal@stekz.com"
                                >legal@stekz.com</a
                            ></strong
                        >
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
// No reactive data needed for static policy page
// You can add analytics tracking or other functionality here if needed
</script>

<style lang="scss" scoped>
.policy-page {
    min-height: 100vh;
    background-color: var(--surface-ground);
    padding: 2rem 1rem;

    @media (max-width: 768px) {
        padding: 1rem 0.5rem;
    }
}

.policy-container {
    max-width: 800px;
    margin: 0 auto;
    background-color: var(--surface-card);
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.policy-header {
    background-color: var(--primary-color);
    color: var(--primary-color-text);
    padding: 2rem;
    text-align: center;

    h1 {
        margin: 0 0 1rem 0;
        font-size: 2.5rem;
        font-weight: 300;

        @media (max-width: 768px) {
            font-size: 2rem;
        }
    }

    .policy-meta {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.9rem;
        opacity: 0.9;

        @media (max-width: 768px) {
            flex-direction: column;
            gap: 0.25rem;

            .separator {
                display: none;
            }
        }

        .version {
            font-weight: 500;
        }
    }
}

.policy-content {
    padding: 2rem;
    line-height: 1.6;
    color: var(--text-color);

    @media (max-width: 768px) {
        padding: 1.5rem;
    }

    h2 {
        color: var(--primary-color);
        margin: 2rem 0 1rem 0;
        padding-bottom: 0.5rem;
        border-bottom: 2px solid var(--surface-border);
    }

    h3 {
        color: var(--text-color);
        margin: 1.5rem 0 0.5rem 0;
    }

    p {
        margin-bottom: 1rem;
    }

    ul {
        margin: 1rem 0;
        padding-left: 1.5rem;

        li {
            margin-bottom: 0.5rem;
        }
    }
}

.placeholder-content {
    em {
        color: var(--text-color-secondary);
        font-size: 1.1rem;
    }
}

.section-suggestions {
    margin-top: 1.5rem;

    .section-item {
        margin-bottom: 1.5rem;
        padding: 1rem;
        background-color: var(--surface-50);
        border-radius: 6px;
        border-left: 4px solid var(--primary-color);

        h3 {
            margin: 0 0 0.5rem 0;
            color: var(--primary-color);
            font-size: 1.1rem;
        }

        .section-description {
            margin: 0;
            color: var(--text-color-secondary);
            font-size: 0.9rem;
        }
    }
}

.policy-footer {
    background-color: var(--surface-50);
    padding: 2rem;
    border-top: 1px solid var(--surface-border);
    text-align: center;

    @media (max-width: 768px) {
        padding: 1.5rem;
    }

    .last-updated {
        margin: 0 0 1rem 0;
        font-size: 0.9rem;
        color: var(--text-color-secondary);
    }

    .contact-info {
        p {
            margin: 0.5rem 0;

            &:first-child {
                color: var(--text-color-secondary);
                font-size: 0.9rem;
            }

            strong {
                color: var(--primary-color);
            }
        }
    }
}
</style>
